import React, { useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { KBarProvider, KBarPortal, KBarPositioner, useKBar, VisualState } from 'kbar'
import { useTheme } from '@mui/material/styles'
import PropTypes from 'prop-types'

import { getCuratedCommands } from '../../utils/kbar-commands-curated'
import KBarClientSearch from './kbar-client-search.component'
import useSiteStore from '../../stores/site.store'
import Search from '../../pages/search/search.component'
import { kbarProviderStyles } from './kbar-provider.styles'

const KBarSearchModal = () => {
  const { query, visualState } = useKBar((state) => ({
    visualState: state.visualState,
  }))
  const theme = useTheme()

  const handleItemClick = useCallback(() => {
    query.toggle()
  }, [query])

  const isVisible = visualState === VisualState.showing || visualState === VisualState.animatingIn

  useEffect(() => {
    if (!isVisible) return

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        query.toggle()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [isVisible, query])

  if (visualState === VisualState.hidden) return null

  return (
    <div style={kbarProviderStyles.searchModal(theme, isVisible)}>
      <div style={kbarProviderStyles.searchContent}>
        <Search onItemClick={handleItemClick} onClose={handleItemClick} />
      </div>
    </div>
  )
}

const KBarProviderComponent = ({ children }) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { siteId: paramSiteId } = useParams()
  const currentSite = useSiteStore((state) => state.currentSite)
  const siteId = paramSiteId || currentSite?.siteId

  const actions = useMemo(() => {
    const curatedCommands = getCuratedCommands(siteId)

    const actionList = curatedCommands.map((command) => ({
      ...command,
      perform: () => {
        const targetPath =
          typeof command.perform === 'function' ? command.perform() : command.perform
        navigate(targetPath)
      },
    }))

    return actionList
  }, [navigate, siteId])

  const positionerStyle = kbarProviderStyles.positioner(theme)

  return (
    <KBarProvider actions={actions}>
      <KBarClientSearch />
      <KBarPortal>
        <KBarPositioner style={positionerStyle}>
          <KBarSearchModal />
        </KBarPositioner>
      </KBarPortal>
      {children}
    </KBarProvider>
  )
}

KBarProviderComponent.propTypes = {
  children: PropTypes.node.isRequired,
}

export default KBarProviderComponent
